import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, firestore } from '../../firebase/config';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  getDoc, 
  updateDoc,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import ChatService from '../../services/ChatService';

const BusinessApplicants = () => {
  const navigate = useNavigate();
  const [applicants, setApplicants] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedJob, setSelectedJob] = useState('all');
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [userProfiles, setUserProfiles] = useState({});
  const [businessProfile, setBusinessProfile] = useState(null);

  useEffect(() => {
    fetchApplicationsAndJobs();
    fetchBusinessProfile();
  }, []);

  const fetchApplicationsAndJobs = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      // Get user's business ID
      const userDoc = await getDoc(doc(firestore, 'users', user.uid));
      if (!userDoc.exists() || !userDoc.data().businessId) return;

      const businessId = userDoc.data().businessId;
      
      // Fetch business jobs
      const jobsQuery = query(
        collection(firestore, 'jobs'),
        where('businessId', '==', businessId)
      );
      const jobsSnapshot = await getDocs(jobsQuery);
      const jobsList = jobsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setJobs(jobsList);

      // Fetch all applications and filter client-side
      console.log('📝 Fetching all applications (filtering client-side until indexes are created)...');
      const allApplicationsSnapshot = await getDocs(collection(firestore, 'jobApplications'));
      const allApplications = allApplicationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
             // Debug: Show all applications and check for our business
       console.log('🔍 Debug: All applications in database:', allApplications);
       console.log('🏢 Looking for business ID:', businessId);
       
       // Check if any applications are missing businessId but might belong to us
       const applicationsWithoutBusinessId = allApplications.filter(app => !app.businessId);
       if (applicationsWithoutBusinessId.length > 0) {
         console.log('⚠️ Found applications without businessId:', applicationsWithoutBusinessId);
       }
       
       // Filter applications for this business
       const applicationsList = allApplications
         .filter(app => app.businessId === businessId)
         .sort((a, b) => {
           const dateA = a.appliedAt?.toDate?.() || new Date(a.appliedAt) || a.createdAt?.toDate?.() || new Date(a.createdAt) || new Date(0);
           const dateB = b.appliedAt?.toDate?.() || new Date(b.appliedAt) || b.createdAt?.toDate?.() || new Date(b.createdAt) || new Date(0);
           return dateB - dateA; // descending order (newest first)
         });
         
       console.log(`📊 Found ${applicationsList.length} applications for business ${businessId}`);

      // Fetch user profiles for applicants
      const userIds = [...new Set(applicationsList.map(app => app.userId))];
      const profiles = {};
      
      console.log(`🔍 Fetching profiles for ${userIds.length} users:`, userIds);
      
      for (const userId of userIds) {
        try {
          let profileData = null;
          
          // First try userProfiles collection (same as regular user)
          const profileDoc = await getDoc(doc(firestore, 'userProfiles', userId));
          if (profileDoc.exists()) {
            profileData = profileDoc.data();
            console.log(`✅ Found profile in userProfiles for ${userId}:`, {
              firstName: profileData.firstName,
              lastName: profileData.lastName,
              hasImagePath: !!profileData.profileImagePath,
              hasImageUrl: !!profileData.profileImageUrl
            });

            // Apply same image logic as regular user
            let finalImageUrl = null;
            
            // Check profileImagePath first (regular user method)
            if (profileData.profileImagePath) {
              if (profileData.profileImagePath.startsWith('http')) {
                // Direct HTTP URL
                finalImageUrl = profileData.profileImagePath;
                console.log(`🔗 Using direct HTTP URL for ${userId}:`, finalImageUrl);
              } else {
                try {
                  // Try Firebase Storage
                  const { ref, getDownloadURL } = await import('firebase/storage');
                  const { storage } = await import('../../firebase/config');
                  
                  const storagePath = profileData.profileImagePath.startsWith('userProfilePhoto/') 
                    ? profileData.profileImagePath 
                    : `userProfilePhoto/${userId}`;
                  
                  const imageRef = ref(storage, storagePath);
                  finalImageUrl = await getDownloadURL(imageRef);
                  console.log(`☁️ Got Firebase Storage URL for ${userId}:`, finalImageUrl);
                } catch (imageError) {
                  console.log(`❌ Firebase Storage failed for ${userId}, trying local asset:`, imageError);
                  // Fallback to local asset path
                  if (profileData.profileImagePath && !profileData.profileImagePath.startsWith('profile_images/')) {
                    finalImageUrl = `/asset/${profileData.profileImagePath}`;
                    console.log(`📁 Using local asset for ${userId}:`, finalImageUrl);
                  }
                }
              }
            }
            
            // If no profileImagePath, try profileImageUrl
            if (!finalImageUrl && profileData.profileImageUrl) {
              finalImageUrl = profileData.profileImageUrl;
              console.log(`🔗 Using profileImageUrl for ${userId}:`, finalImageUrl);
            }
            
            // Set the final image URL
            profileData.profileImageUrl = finalImageUrl;
            
          } else {
            console.log(`❌ No profile in userProfiles for ${userId}, checking users collection...`);
            
            // Fallback to users collection
            const userDoc = await getDoc(doc(firestore, 'users', userId));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              profileData = {
                firstName: userData.firstName || '',
                lastName: userData.lastName || '',
                email: userData.email || '',
                phone: userData.phone || '',
                bio: userData.bio || '',
                skills: userData.skills || '',
                experience: userData.experience || '',
                education: userData.education || '',
                availability: userData.availability || '',
                location: userData.location || '',
                // Apply same logic for users collection
                profileImagePath: userData.profileImagePath || '',
                profileImageUrl: userData.profileImageUrl || 
                               userData.photoURL || 
                               userData.profilePicture || 
                               userData.avatar || 
                               userData.image || 
                               userData.picture || 
                               userData.profileImage || ''
              };
              
              // Apply same image processing for users collection
              let finalImageUrl = null;
              
              if (profileData.profileImagePath) {
                if (profileData.profileImagePath.startsWith('http')) {
                  finalImageUrl = profileData.profileImagePath;
                } else {
                  try {
                    const { ref, getDownloadURL } = await import('firebase/storage');
                    const { storage } = await import('../../firebase/config');
                    
                    const storagePath = profileData.profileImagePath.startsWith('userProfilePhoto/') 
                      ? profileData.profileImagePath 
                      : `userProfilePhoto/${userId}`;
                    
                    const imageRef = ref(storage, storagePath);
                    finalImageUrl = await getDownloadURL(imageRef);
                  } catch (imageError) {
                    if (profileData.profileImagePath && !profileData.profileImagePath.startsWith('profile_images/')) {
                      finalImageUrl = `/asset/${profileData.profileImagePath}`;
                    }
                  }
                }
              }
              
              if (!finalImageUrl && profileData.profileImageUrl) {
                finalImageUrl = profileData.profileImageUrl;
              }
              
              profileData.profileImageUrl = finalImageUrl;
              
              console.log(`✅ Found fallback profile in users for ${userId}:`, {
                firstName: profileData.firstName,
                lastName: profileData.lastName,
                hasImagePath: !!userData.profileImagePath,
                hasImageUrl: !!finalImageUrl,
                finalImageUrl: finalImageUrl
              });
            } else {
              console.log(`❌ No profile found anywhere for user ${userId}`);
            }
          }
          
          if (profileData) {
            profiles[userId] = profileData;
          }
        } catch (error) {
          console.error(`❌ Error fetching profile for user ${userId}:`, error);
        }
      }
      
      console.log(`📊 Final profiles summary:`, Object.keys(profiles).map(userId => ({
        userId,
        name: `${profiles[userId].firstName} ${profiles[userId].lastName}`,
        hasImage: !!profiles[userId].profileImageUrl,
        imageUrl: profiles[userId].profileImageUrl || 'No image'
      })));

      // Test image accessibility
      const profilesWithImages = Object.keys(profiles).filter(userId => profiles[userId].profileImageUrl);
      console.log(`🖼️ Testing image accessibility for ${profilesWithImages.length} profiles with images...`);
      
      profilesWithImages.forEach(async (userId, index) => {
        const profile = profiles[userId];
        if (profile.profileImageUrl) {
          try {
            const testImg = new Image();
            testImg.onload = () => {
              console.log(`✅ Image loads successfully for ${profile.firstName} ${profile.lastName}: ${profile.profileImageUrl}`);
            };
            testImg.onerror = () => {
              console.log(`❌ Image failed to load for ${profile.firstName} ${profile.lastName}: ${profile.profileImageUrl}`);
            };
            testImg.src = profile.profileImageUrl;
          } catch (error) {
            console.log(`❌ Error testing image for ${profile.firstName} ${profile.lastName}:`, error);
          }
        }
      });
      
      setUserProfiles(profiles);
      setApplicants(applicationsList);
    } catch (error) {
      console.error('Error fetching applications and jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBusinessProfile = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      const userDoc = await getDoc(doc(firestore, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.businessId) {
          const businessDoc = await getDoc(doc(firestore, 'businesses', userData.businessId));
          if (businessDoc.exists()) {
            setBusinessProfile({ id: userData.businessId, ...businessDoc.data() });
          }
        }
      }
    } catch (error) {
      console.error('Error fetching business profile:', error);
    }
  };

  const handleStatusUpdate = async (applicationId, newStatus) => {
    try {
      await updateDoc(doc(firestore, 'jobApplications', applicationId), {
        status: newStatus,
        updatedAt: serverTimestamp()
      });
      
      // Update local state
      setApplicants(prev => 
        prev.map(app => 
          app.id === applicationId 
            ? { ...app, status: newStatus }
            : app
        )
      );
      
      // Update selectedApplicant if it's the one being updated
      if (selectedApplicant && selectedApplicant.id === applicationId) {
        setSelectedApplicant(prev => ({
          ...prev,
          status: newStatus
        }));
      }
      
              // alert(`Application status updated to ${newStatus}`); // Disabled notification
    } catch (error) {
      console.error('Error updating application status:', error);
      alert('Error updating status. Please try again.');
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown date';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return phone;
  };

  const getJobTitle = (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    return job?.title || 'Unknown Position';
  };

  const filteredApplicants = applicants.filter(applicant => {
    // Filter by status
    if (filter !== 'all' && applicant.status !== filter) return false;
    
    // Filter by job
    if (selectedJob !== 'all' && applicant.jobId !== selectedJob) return false;
    
    return true;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-gradient-to-r from-yellow-50 to-yellow-100 text-yellow-700 border border-yellow-200/50';
      case 'reviewing': return 'bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 border border-blue-200/50';
      case 'interviewed': return 'bg-gradient-to-r from-purple-50 to-purple-100 text-purple-700 border border-purple-200/50';
      case 'hired': return 'bg-gradient-to-r from-green-50 to-green-100 text-green-700 border border-green-200/50';
      case 'rejected': return 'bg-gradient-to-r from-red-50 to-red-100 text-red-700 border border-red-200/50';
      default: return 'bg-gradient-to-r from-gray-50 to-gray-100 text-gray-700 border border-gray-200/50';
    }
  };

  const startChat = async (applicant) => {
    try {
      const user = auth.currentUser;
      if (!user || !businessProfile) {
        alert('Unable to start chat. Please try again.');
        return;
      }

      console.log('💬 Starting chat with applicant:', applicant.userId);
      console.log('🏢 Current business manager user ID:', user.uid);
      console.log('📄 Job ID:', applicant.jobId);

      // Create or get existing chat room between business manager and job seeker
      const roomId = await ChatService.createOrGetChatRoom(user.uid, applicant.userId);

      // Send initial message about the job application
      if (applicant.jobId) {
        const jobTitle = getJobTitle(applicant.jobId);
        const profile = userProfiles[applicant.userId] || {};
        const initialMessage = `Hi ${profile.firstName || 'there'}! I saw your application for the ${jobTitle} position. I'd like to discuss this opportunity with you.`;
        
        await ChatService.sendMessage(roomId, user.uid, initialMessage);
      }

      console.log('✅ Chat room created:', roomId);
      
      // Navigate to business chat page with the specific room
      navigate(`/business-dashboard/chat?room=${roomId}`);
      setShowDetailModal(false);
      
    } catch (error) {
      console.error('Error starting chat:', error);
      alert(`Failed to start chat: ${error.message}. Please try again.`);
    }
  };

  const ApplicantDetailModal = () => {
    if (!selectedApplicant || !showDetailModal) return null;

    const profile = userProfiles[selectedApplicant.userId] || {};
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden shadow-2xl">
          {/* Enhanced Header */}
          <div className="bg-gradient-to-r from-[#FF8C00] via-[#FF7A00] to-[#FF6B35] px-8 py-6 relative overflow-hidden">
            <div className="absolute inset-0 bg-black opacity-5"></div>
            <div className="relative flex justify-between items-center">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white drop-shadow-sm">
                    Application Details
                  </h3>
                  <p className="text-white/80 text-sm">
                    Review candidate information and manage application status
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-xl flex items-center justify-center transition-all duration-200 backdrop-blur-sm group"
              >
                <svg className="w-5 h-5 text-white group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="overflow-y-auto max-h-[calc(95vh-120px)] bg-gray-50">
            <div className="p-8 space-y-8">
              {/* Candidate Profile Card */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 px-6 py-4 border-b border-gray-100">
                  <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                    Candidate Information
                  </h4>
                </div>
                <div className="p-6">
                  <div className="flex items-start space-x-6">
                    <div className="relative flex-shrink-0">
                      {profile.profileImageUrl ? (
                        <div className="relative">
                          <img 
                            src={profile.profileImageUrl} 
                            alt={`${profile.firstName} ${profile.lastName}`}
                            className="w-20 h-20 rounded-2xl object-cover border-4 border-white shadow-lg"
                            onError={(e) => {
                              console.log(`❌ Failed to load modal image for ${profile.firstName} ${profile.lastName}: ${profile.profileImageUrl}`);
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <div 
                            className="w-20 h-20 bg-gradient-to-br from-[#FF8C00] via-[#FF7A00] to-[#FF6B35] rounded-2xl flex items-center justify-center shadow-lg border-4 border-white absolute inset-0" 
                            style={{display: 'none'}}
                          >
                            <span className="text-white font-bold text-2xl drop-shadow-sm">
                              {profile.firstName?.[0]?.toUpperCase() || profile.lastName?.[0]?.toUpperCase() || '?'}
                            </span>
                          </div>
                          <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 border-3 border-white rounded-full"></div>
                        </div>
                      ) : (
                        <div className="relative">
                          <div className="w-20 h-20 bg-gradient-to-br from-[#FF8C00] via-[#FF7A00] to-[#FF6B35] rounded-2xl flex items-center justify-center shadow-lg border-4 border-white">
                            <span className="text-white font-bold text-2xl drop-shadow-sm">
                              {profile.firstName?.[0]?.toUpperCase() || profile.lastName?.[0]?.toUpperCase() || '?'}
                            </span>
                          </div>
                          <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 border-3 border-white rounded-full"></div>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="text-2xl font-bold text-gray-900 mb-1">
                            {profile.firstName} {profile.lastName}
                          </h4>
                          <div className="space-y-2 mb-4">
                            <div className="flex items-center text-gray-600">
                              <svg className="w-4 h-4 mr-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                              </svg>
                              {profile.email}
                            </div>
                            {profile.phone && (
                              <div className="flex items-center text-gray-600">
                                <svg className="w-4 h-4 mr-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                                </svg>
                                {formatPhoneNumber(profile.phone)}
                              </div>
                            )}
                            {profile.location && (
                              <div className="flex items-center text-gray-600">
                                <svg className="w-4 h-4 mr-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                </svg>
                                {profile.location}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="ml-6">
                          <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(selectedApplicant.status)} border-2 border-current border-opacity-20`}>
                            <div className="w-2 h-2 rounded-full mr-2 bg-current opacity-75"></div>
                            {selectedApplicant.status === 'pending' && 'Pending Review'}
                            {selectedApplicant.status === 'reviewing' && 'Under Review'}
                            {selectedApplicant.status === 'interviewed' && 'Interviewed'}
                            {selectedApplicant.status === 'hired' && 'Hired'}
                            {selectedApplicant.status === 'rejected' && 'Rejected'}
                            {!selectedApplicant.status && 'Pending Review'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Application Details Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Column */}
                <div className="space-y-6">
                  {/* Job Information */}
                  <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4 border-b border-gray-100">
                      <h5 className="font-semibold text-gray-900 flex items-center">
                        <svg className="w-5 h-5 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
                        </svg>
                        Applied Position
                      </h5>
                    </div>
                    <div className="p-6">
                      <h6 className="text-xl font-bold text-gray-900 mb-2">{getJobTitle(selectedApplicant.jobId)}</h6>
                      <div className="flex items-center text-gray-600 text-sm">
                        <svg className="w-4 h-4 mr-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                        </svg>
                        Applied on {formatDate(selectedApplicant.appliedAt) || 'Unknown date'}
                      </div>
                    </div>
                  </div>

                  {/* Skills */}
                  {profile.skills && (
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                      <div className="bg-gradient-to-r from-purple-50 to-pink-50 px-6 py-4 border-b border-gray-100">
                        <h5 className="font-semibold text-gray-900 flex items-center">
                          <svg className="w-5 h-5 mr-2 text-purple-500" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Skills & Expertise
                        </h5>
                      </div>
                      <div className="p-6">
                        <div className="flex flex-wrap gap-2">
                          {(() => {
                            let skillsArray = [];
                            
                            if (typeof profile.skills === 'string') {
                              skillsArray = profile.skills.split(',');
                            } else if (Array.isArray(profile.skills)) {
                              skillsArray = profile.skills;
                            } else {
                              skillsArray = String(profile.skills).split(',');
                            }
                            
                            return skillsArray.map((skill, index) => (
                              <span 
                                key={index}
                                className="px-3 py-2 bg-gradient-to-r from-blue-100 to-blue-50 text-blue-800 text-sm rounded-xl font-medium border border-blue-200 hover:from-blue-200 hover:to-blue-100 transition-all duration-200"
                              >
                                {String(skill).trim()}
                              </span>
                            ));
                          })()}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Availability */}
                  {profile.availability && (
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 px-6 py-4 border-b border-gray-100">
                        <h5 className="font-semibold text-gray-900 flex items-center">
                          <svg className="w-5 h-5 mr-2 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                          </svg>
                          Availability
                        </h5>
                      </div>
                      <div className="p-6">
                        <p className="text-gray-700 bg-emerald-50 rounded-xl p-3 border border-emerald-100">{profile.availability}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                  {/* Application Message */}
                  {selectedApplicant.message && (
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                      <div className="bg-gradient-to-r from-yellow-50 to-orange-50 px-6 py-4 border-b border-gray-100">
                        <h5 className="font-semibold text-gray-900 flex items-center">
                          <svg className="w-5 h-5 mr-2 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                          </svg>
                          Cover Letter / Message
                        </h5>
                      </div>
                      <div className="p-6">
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                          <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{selectedApplicant.message}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Experience */}
                  {profile.experience && (
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                      <div className="bg-gradient-to-r from-indigo-50 to-blue-50 px-6 py-4 border-b border-gray-100">
                        <h5 className="font-semibold text-gray-900 flex items-center">
                          <svg className="w-5 h-5 mr-2 text-indigo-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
                          </svg>
                          Work Experience
                        </h5>
                      </div>
                      <div className="p-6">
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                          <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{profile.experience}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Status Update */}
                  <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-red-50 to-pink-50 px-6 py-4 border-b border-gray-100">
                      <h5 className="font-semibold text-gray-900 flex items-center">
                        <svg className="w-5 h-5 mr-2 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V4z" clipRule="evenodd" />
                        </svg>
                        Update Application Status
                      </h5>
                    </div>
                    <div className="p-6">
                      <div className="grid grid-cols-1 gap-3">
                        {[
                          { key: 'pending', label: 'Pending Review', icon: '⏳', color: 'yellow' },
                          { key: 'reviewing', label: 'Under Review', icon: '👀', color: 'blue' },
                          { key: 'interviewed', label: 'Interviewed', icon: '🗣️', color: 'purple' },
                          { key: 'hired', label: 'Hired', icon: '✅', color: 'green' },
                          { key: 'rejected', label: 'Rejected', icon: '❌', color: 'red' }
                        ].map(status => {
                          const isActive = selectedApplicant.status === status.key;
                          return (
                            <button
                              key={status.key}
                              onClick={() => handleStatusUpdate(selectedApplicant.id, status.key)}
                              className={`flex items-center px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 border-2 ${
                                isActive
                                  ? 'bg-[#FF8C00] text-white border-[#FF8C00] shadow-lg transform scale-105'
                                  : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100 hover:border-gray-300 hover:scale-102'
                              }`}
                            >
                              <span className="mr-3 text-lg">{status.icon}</span>
                              {status.label}
                              {isActive && (
                                <svg className="w-4 h-4 ml-auto" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      setShowProfileModal(true);
                    }}
                    className="flex items-center justify-center px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 font-medium"
                  >
                    <svg className="w-5 h-5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                    View Full Profile
                  </button>
                  <button
                    onClick={() => startChat(selectedApplicant)}
                    className="flex items-center justify-center px-6 py-4 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 font-medium"
                  >
                    <svg className="w-5 h-5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                      <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                    </svg>
                    Start Chat
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const UserProfileModal = () => {
    if (!selectedApplicant || !showProfileModal) return null;

    const profile = userProfiles[selectedApplicant.userId] || {};
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-medium text-gray-900">
                User Profile - {profile.firstName} {profile.lastName}
              </h3>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    setShowProfileModal(false);
                    setShowDetailModal(true);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ← Back
                </button>
                <button
                  onClick={() => setShowProfileModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="space-y-6">
              {/* Header with Photo and Basic Info */}
              <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg p-6 text-white">
                <div className="flex items-center space-x-6">
                  <div className="w-24 h-24 rounded-full bg-white bg-opacity-20 overflow-hidden flex-shrink-0 relative">
                    {profile.profileImageUrl ? (
                      <>
                        <img 
                          src={profile.profileImageUrl} 
                          alt={`${profile.firstName} ${profile.lastName}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            console.log(`❌ Failed to load UserProfile modal image for ${profile.firstName} ${profile.lastName}: ${profile.profileImageUrl}`);
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div 
                          className="w-full h-full flex items-center justify-center text-white font-bold text-2xl absolute inset-0"
                          style={{display: 'none'}}
                        >
                          {profile.firstName?.[0]?.toUpperCase() || profile.lastName?.[0]?.toUpperCase() || '?'}
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white font-bold text-2xl">
                        {profile.firstName?.[0]?.toUpperCase() || profile.lastName?.[0]?.toUpperCase() || '?'}
                      </div>
                    )}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">{profile.firstName} {profile.lastName}</h2>
                    <p className="text-blue-100 mt-1">{profile.email}</p>
                    {profile.phone && (
                      <p className="text-blue-100">{profile.phone}</p>
                    )}
                    {profile.location && (
                      <p className="text-blue-100">📍 {profile.location}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Professional Summary */}
              {profile.bio && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Professional Summary</h4>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-gray-700 whitespace-pre-wrap">{profile.bio}</p>
                  </div>
                </div>
              )}

              {/* Experience */}
              {profile.experience && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Work Experience</h4>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-gray-700 whitespace-pre-wrap">{profile.experience}</p>
                  </div>
                </div>
              )}

              {/* Skills */}
              {profile.skills && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Skills</h4>
                  <div className="flex flex-wrap gap-2">
                    {(() => {
                      // Handle different skill data types
                      let skillsArray = [];
                      
                      if (typeof profile.skills === 'string') {
                        // If skills is a string, split by comma
                        skillsArray = profile.skills.split(',');
                      } else if (Array.isArray(profile.skills)) {
                        // If skills is already an array, use it directly
                        skillsArray = profile.skills;
                      } else {
                        // If skills is neither string nor array, convert to string and split
                        skillsArray = String(profile.skills).split(',');
                      }
                      
                      return skillsArray.map((skill, index) => (
                        <span 
                          key={index}
                          className="px-3 py-2 bg-blue-100 text-blue-800 text-sm rounded-lg font-medium"
                        >
                          {String(skill).trim()}
                        </span>
                      ));
                    })()}
                  </div>
                </div>
              )}

              {/* Education */}
              {profile.education && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Education</h4>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-gray-700 whitespace-pre-wrap">{profile.education}</p>
                  </div>
                </div>
              )}

              {/* Certifications */}
              {profile.certifications && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Certifications</h4>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-gray-700 whitespace-pre-wrap">{profile.certifications}</p>
                  </div>
                </div>
              )}

              {/* Availability */}
              {profile.availability && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Availability</h4>
                  <div className="bg-green-50 rounded-lg p-4">
                    <p className="text-green-800 font-medium">{profile.availability}</p>
                  </div>
                </div>
              )}

              {/* Work Schedule Preferences */}
              {profile.workSchedule && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Preferred Work Schedule</h4>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-gray-700">{profile.workSchedule}</p>
                  </div>
                </div>
              )}

              {/* Emergency Contact */}
              {(profile.emergencyContactName || profile.emergencyContactPhone) && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Emergency Contact</h4>
                  <div className="bg-red-50 rounded-lg p-4">
                    {profile.emergencyContactName && (
                      <p className="text-red-800 font-medium">{profile.emergencyContactName}</p>
                    )}
                    {profile.emergencyContactPhone && (
                      <p className="text-red-700">{profile.emergencyContactPhone}</p>
                    )}
                    {profile.emergencyContactRelationship && (
                      <p className="text-red-600 text-sm">Relationship: {profile.emergencyContactRelationship}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Application Message */}
              {selectedApplicant.message && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Application Message</h4>
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                    <p className="text-yellow-800 whitespace-pre-wrap">{selectedApplicant.message}</p>
                    <p className="text-yellow-600 text-sm mt-2">
                      Applied for: {getJobTitle(selectedApplicant.jobId)} on {formatDate(selectedApplicant.appliedAt)}
                    </p>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="border-t pt-6">
                <div className="flex space-x-4">
                  <button
                    onClick={() => startChat(selectedApplicant)}
                    className="flex-1 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 font-medium"
                  >
                    💬 Start Chat
                  </button>
                  <button
                    onClick={() => {
                      setShowProfileModal(false);
                      setShowDetailModal(true);
                    }}
                    className="flex-1 bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 font-medium"
                  >
                    📋 Back to Application
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Applicants</h1>
        <p className="text-gray-600">Review and manage job applications.</p>
      </div>

      {/* Application Filters */}
      <div className="mb-8 bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col space-y-6">
          {/* Header */}
          <div className="flex items-center">
            <div className="w-8 h-8 bg-[#FF8C00]/10 rounded-xl flex items-center justify-center mr-3">
              <svg className="w-4 h-4 text-[#FF8C00]" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
              </svg>
            </div>
            <span className="text-sm font-semibold text-gray-900">Filter Applications</span>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Status</label>
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'all', label: 'All', icon: '📋', color: 'gray' },
                { key: 'pending', label: 'Pending', icon: '⏳', color: 'yellow' },
                { key: 'reviewing', label: 'Reviewing', icon: '👀', color: 'blue' },
                { key: 'interviewed', label: 'Interviewed', icon: '🗣️', color: 'purple' },
                { key: 'hired', label: 'Hired', icon: '✅', color: 'green' },
                { key: 'rejected', label: 'Rejected', icon: '❌', color: 'red' }
              ].map((filterOption) => {
                const count = filterOption.key === 'all' ? applicants.length : applicants.filter(a => a.status === filterOption.key).length;
                const isActive = filter === filterOption.key;
                
                return (
                  <button
                    key={filterOption.key}
                    onClick={() => setFilter(filterOption.key)}
                    className={`inline-flex items-center px-4 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 ${
                      isActive
                        ? 'bg-[#FF8C00] text-white shadow-md transform scale-105'
                        : 'bg-slate-50 text-gray-700 hover:bg-gray-100 hover:scale-102'
                    }`}
                  >
                    <span className="mr-2">{filterOption.icon}</span>
                    {filterOption.label}
                    <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                      isActive 
                        ? 'bg-white/20 text-white' 
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Job Filter */}
          {jobs.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Job Position</label>
              <select
                value={selectedJob}
                onChange={(e) => setSelectedJob(e.target.value)}
                className="w-full max-w-md border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#FF8C00]/20 focus:border-[#FF8C00] bg-white transition-all"
              >
                <option value="all">All Jobs ({applicants.length} total applications)</option>
                {jobs.map(job => (
                  <option key={job.id} value={job.id}>
                    {job.title} ({applicants.filter(a => a.jobId === job.id).length} applications)
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Summary Stats */}
          <div className="pt-4 border-t border-gray-100">
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span>
                Showing {filteredApplicants.length} of {applicants.length} applications
              </span>
              <div className="flex items-center space-x-4">
                <span className="flex items-center">
                  <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                  {Math.round((applicants.filter(app => app.status === 'hired').length / applicants.length) * 100) || 0}% hire rate
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Applicants List */}
      {filteredApplicants.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-center py-8">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <p className="mt-2 text-sm text-gray-500">
              {filter === 'all' && selectedJob === 'all' 
                ? 'No applicants yet' 
                : 'No applicants match your current filters'}
            </p>
            <p className="text-sm text-gray-500">
              {filter === 'all' && selectedJob === 'all'
                ? 'Post jobs to start receiving applications'
                : 'Try adjusting your filters to see more results'}
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-white shadow-lg rounded-2xl overflow-hidden border border-gray-200">
          <ul className="divide-y divide-gray-100">
            {filteredApplicants.map((applicant) => {
              const profile = userProfiles[applicant.userId] || {};
              return (
                <li key={applicant.id} className="hover:bg-gradient-to-r hover:from-[#FF8C00]/5 hover:to-transparent transition-all duration-200">
                                     <div className="px-6 py-5 sm:px-8">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-6 flex-1">
                        {/* Enhanced Profile Picture */}
                        <div className="relative flex-shrink-0">
                          {profile.profileImageUrl ? (
                            <div className="relative">
                              <img 
                                src={profile.profileImageUrl} 
                                alt={`${profile.firstName} ${profile.lastName}`}
                                className="w-16 h-16 rounded-2xl object-cover border-3 border-white shadow-lg"
                                onError={(e) => {
                                  console.log(`❌ Failed to load image for ${profile.firstName} ${profile.lastName}: ${profile.profileImageUrl}`);
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                              <div 
                                className="w-16 h-16 bg-gradient-to-br from-[#FF8C00] via-[#FF7A00] to-[#FF6B35] rounded-2xl flex items-center justify-center shadow-lg border-3 border-white absolute inset-0" 
                                style={{display: 'none'}}
                              >
                                <span className="text-white font-bold text-xl drop-shadow-sm">
                                  {profile.firstName?.[0]?.toUpperCase() || '?'}
                                </span>
                              </div>
                              <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 border-2 border-white rounded-full"></div>
                            </div>
                          ) : (
                            <div className="relative">
                              <div className="w-16 h-16 bg-gradient-to-br from-[#FF8C00] via-[#FF7A00] to-[#FF6B35] rounded-2xl flex items-center justify-center shadow-lg border-3 border-white">
                                <span className="text-white font-bold text-xl drop-shadow-sm">
                                  {profile.firstName?.[0]?.toUpperCase() || profile.lastName?.[0]?.toUpperCase() || '?'}
                                </span>
                              </div>
                              <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 border-2 border-white rounded-full"></div>
                            </div>
                          )}
                        </div>

                        {/* Applicant Information */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <h3 className="text-xl font-bold text-gray-900 truncate hover:text-[#FF8C00] transition-colors">
                                {profile.firstName} {profile.lastName}
                              </h3>
                              <p className="text-gray-600 text-sm mt-1">
                                {profile.email}
                              </p>
                              {/* Job Position Badge */}
                              <div className="mt-2">
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-[#FF8C00]/10 text-[#FF8C00] border border-[#FF8C00]/20">
                                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
                                    <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" />
                                  </svg>
                                  Applied for: {getJobTitle(applicant.jobId)}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Application Info */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            <div className="flex items-center text-sm text-gray-600">
                              <div className="w-8 h-8 bg-blue-50 rounded-xl flex items-center justify-center mr-3">
                                <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div>
                                <p className="font-medium">Applied</p>
                                <p className="text-xs text-gray-500">
                                  {applicant.appliedAt ? (() => {
                                    const date = applicant.appliedAt.toDate ? applicant.appliedAt.toDate() : new Date(applicant.appliedAt);
                                    const now = new Date();
                                    const diffInHours = (now - date) / (1000 * 60 * 60);
                                    
                                    if (diffInHours < 1) {
                                      const diffInMinutes = Math.floor((now - date) / (1000 * 60));
                                      return diffInMinutes < 1 ? 'just now' : `${diffInMinutes}m ago`;
                                    } else if (diffInHours < 24) {
                                      return `${Math.floor(diffInHours)}h ago`;
                                    } else {
                                      const diffInDays = Math.floor(diffInHours / 24);
                                      return diffInDays === 1 ? '1 day ago' : `${diffInDays} days ago`;
                                    }
                                  })() : 'Recently'}
                                </p>
                              </div>
                            </div>

                            {profile.phone && (
                              <div className="flex items-center text-sm text-gray-600">
                                <div className="w-8 h-8 bg-green-50 rounded-xl flex items-center justify-center mr-3">
                                  <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                                  </svg>
                                </div>
                                                                 <div>
                                   <p className="font-medium">Phone</p>
                                   <p className="text-xs text-gray-500">{formatPhoneNumber(profile.phone)}</p>
                                 </div>
                              </div>
                            )}

                            <div className="flex items-center text-sm text-gray-600">
                              <div className="w-8 h-8 bg-purple-50 rounded-xl flex items-center justify-center mr-3">
                                <svg className="w-4 h-4 text-purple-500" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div>
                                <p className="font-medium">Status</p>
                                <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full ${getStatusColor(applicant.status)}`}>
                                  <div className="w-1.5 h-1.5 rounded-full mr-1.5 bg-current opacity-75"></div>
                                  {applicant.status || 'Pending'}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Cover Letter Preview */}
                          {applicant.message && (
                            <div className="mb-4">
                              <div className="bg-gradient-to-r from-gray-50 to-gray-50/30 rounded-xl p-4 border border-gray-100">
                                <div className="flex items-center mb-2">
                                  <div className="w-6 h-6 bg-[#FF8C00]/10 rounded-lg flex items-center justify-center mr-2">
                                    <svg className="w-3 h-3 text-[#FF8C00]" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                    </svg>
                                  </div>
                                  <h4 className="text-sm font-semibold text-gray-900">Cover Letter</h4>
                                </div>
                                <p className="text-gray-700 text-sm leading-relaxed" style={{
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden'
                                }}>
                                  {applicant.message}
                                </p>
                                {applicant.message.length > 100 && (
                                  <button className="text-[#FF8C00] text-xs mt-2 hover:underline font-medium">
                                    Read more
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Action Panel */}
                      <div className="flex flex-col items-end space-y-3 ml-6">
                        {/* Status Selector */}
                        <select
                          value={applicant.status || 'pending'}
                          onChange={(e) => handleStatusUpdate(applicant.id, e.target.value)}
                          className="text-sm border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#FF8C00]/20 focus:border-[#FF8C00] bg-white transition-all min-w-[160px]"
                        >
                          <option value="pending">📋 Pending</option>
                          <option value="reviewing">👀 Reviewing</option>
                          <option value="interviewed">🗣️ Interviewed</option>
                          <option value="hired">✅ Hired</option>
                          <option value="rejected">❌ Rejected</option>
                        </select>
                        
                        {/* Action Buttons */}
                        <div className="flex items-center space-x-2">
                          <button 
                            onClick={() => {
                              setSelectedApplicant(applicant);
                              setShowDetailModal(true);
                            }}
                            className="px-4 py-2 bg-blue-500 text-white text-sm rounded-xl hover:bg-blue-600 transition-all hover:scale-105 shadow-sm flex items-center"
                          >
                            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                              <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                            </svg>
                            Details
                          </button>
                          
                          <button 
                            onClick={() => {
                              setSelectedApplicant(applicant);
                              setShowProfileModal(true);
                            }}
                            className="px-4 py-2 bg-[#FF8C00] text-white text-sm rounded-xl hover:bg-[#FF8C00]/90 transition-all hover:scale-105 shadow-sm flex items-center"
                          >
                            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                            </svg>
                            Profile
                          </button>
                          
                          <button 
                            onClick={() => startChat(applicant)}
                            className="px-4 py-2 bg-green-500 text-white text-sm rounded-xl hover:bg-green-600 transition-all hover:scale-105 shadow-sm flex items-center"
                          >
                            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                              <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                            </svg>
                            Chat
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Progress Indicator */}
                    <div className="mt-4">
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full transition-all duration-700 ease-out rounded-full ${
                          applicant.status === 'pending' ? 'w-1/5 bg-gradient-to-r from-yellow-400 to-yellow-500' :
                          applicant.status === 'reviewing' ? 'w-2/5 bg-gradient-to-r from-blue-400 to-blue-500' :
                          applicant.status === 'interviewed' ? 'w-3/5 bg-gradient-to-r from-purple-400 to-purple-500' :
                          applicant.status === 'hired' ? 'w-full bg-gradient-to-r from-green-400 to-green-500' :
                          applicant.status === 'rejected' ? 'w-full bg-gradient-to-r from-red-400 to-red-500' :
                          'w-1/5 bg-gradient-to-r from-yellow-400 to-yellow-500'
                        }`}></div>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Applicant Detail Modal */}
      <ApplicantDetailModal />

      {/* User Profile Modal */}
      <UserProfileModal />
    </div>
  );
};

export default BusinessApplicants; 